import PropTypes from 'prop-types';
import { useTheme } from '@mui/material/styles';
import Stack from '@mui/material/Stack';
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import IconButton from '@mui/material/IconButton';
import { bgBlur } from 'src/theme/css';
import { useOffSetTop } from 'src/hooks/use-off-set-top';
import { useResponsive } from 'src/hooks/use-responsive';
import Logo from 'src/components/logo';
import SvgColor from 'src/components/svg-color';
import { useSettingsContext } from 'src/components/settings';
import { Box } from '@mui/system';
import { useSelector, useDispatch } from 'react-redux';
import { HEADER } from '../config-layout';
import { Searchbar, AccountPopover, SettingsButton } from '../_common';

// ----------------------------------------------------------------------

export default function Header({ onOpenNav }) {
  const theme = useTheme();
  const settings = useSettingsContext();
  const isNavHorizontal = settings.themeLayout === 'horizontal';
  const lgUp = useResponsive('up', 'lg');
  const offset = useOffSetTop(HEADER.H_DESKTOP);
  const drawerWidth = useSelector((state) => state.drawer.drawerWidth);

  const logoVariant = !lgUp || drawerWidth < 110 ? 'short' : 'full';

  const renderContent = (
    <>
      {!lgUp && (
        <IconButton onClick={onOpenNav} sx={{ mr: 1 }}>
          <SvgColor src="/assets/icons/navbar/ic_menu_item.svg" />
        </IconButton>
      )}

      <Box
        sx={{
          width: lgUp ? drawerWidth : 'auto',
          display: 'flex',
          justifyContent: lgUp ? 'center' : 'flex-start',
          alignItems: 'center',
        }}
      >
        <Logo
          variant={logoVariant}
          sx={{
            flexShrink: 0,
            height: 32,
            width: 'auto',
            margin: '0 auto',
            objectPosition: 'center',
          }}
        />
      </Box>

      <Searchbar />

      <Stack
        flexGrow={1}
        direction="row"
        alignItems="center"
        justifyContent="flex-end"
        sx={{
          height: 40,
          mt: 0,
          '& > *': {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          },
          '& .MuiIconButton-root': {
            width: 40,
            height: 40,
            p: 0,
          },
          '& .MuiBadge-root': {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          },
        }}
        spacing={{ xs: 0.5, sm: 1 }}
      >

        <SettingsButton />

        <AccountPopover />
      </Stack>
    </>
  );

  return (
    <AppBar
      sx={{
        height: HEADER.H_MOBILE,
        zIndex: theme.zIndex.appBar + 1,
        ...bgBlur({
          color: theme.palette.background.paper,
        }),
        transition: theme.transitions.create(['height'], {
          duration: theme.transitions.duration.shorter,
        }),
        ...(lgUp && {
          width: 1, // Full width
          height: HEADER.H_DESKTOP,
          ...(isNavHorizontal && {
            bgcolor: 'background.default',
            borderBottom: `dashed 1px ${theme.palette.divider}`,
          }),
        }),
      }}
    >
      <Toolbar
        sx={{
          height: '100%',
          minHeight: `${HEADER.H_DESKTOP}px !important`,
          pl: { xs: 2, lg: 0 },
          pr: { xs: 2, lg: 5 },
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          py: 0,
        }}
      >
        {renderContent}
      </Toolbar>
    </AppBar>
  );
}

Header.propTypes = {
  onOpenNav: PropTypes.func,
};
